import { useMemo, useState } from 'react';
import { CheckCircle2Icon, CircleAlertIcon, MapPinIcon, RadioIcon, UserRoundIcon } from 'lucide-react';
import type { TwinPresenceController } from '@/hooks/use-twin-presence';
import { validateAvatarDisplayName } from '@/lib/profanity';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#db2777', '#dc2626', '#d97706', '#059669', '#0891b2'] as const;
const UPDATE_INTERVALS = [1, 5, 10, 30] as const;

export interface TwinAvatarCustomizerProps {
  sceneLabel: string;
  presence: TwinPresenceController;
  className?: string;
}

export function TwinAvatarCustomizer({ sceneLabel, presence, className }: TwinAvatarCustomizerProps) {
  const [open, setOpen] = useState(false);
  const nameValidation = useMemo(() => validateAvatarDisplayName(presence.displayName), [presence.displayName]);
  const { locationTrack } = presence;

  const accuracyLabel = locationTrack.location
    ? `Accurate to about ${Math.round(locationTrack.location.accuracyMeters)} m`
    : locationTrack.status === 'requesting'
      ? 'Finding your location…'
      : 'Location is not being shared';
  const coordinatesLabel = locationTrack.location
    ? `${locationTrack.location.latitude.toFixed(5)}, ${locationTrack.location.longitude.toFixed(5)}`
    : null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button type="button" size="sm" variant="outline" className={cn('gap-1.5', className)}>
          <UserRoundIcon className="size-3.5" aria-hidden />
          <span className="hidden sm:inline">My avatar</span>
          {presence.isSharing && <span className="size-2 rounded-full bg-emerald-500" aria-label="Location sharing active" />}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>My digital twin avatar</SheetTitle>
          <SheetDescription>
            Choose how you appear in {sceneLabel}. Your device location is only shared after you opt in.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <section className="space-y-2" aria-labelledby="avatar-name-heading">
            <div className="flex items-center justify-between gap-3">
              <Label id="avatar-name-heading" htmlFor="twin-avatar-display-name">Display name</Label>
              <span className="text-xs text-muted-foreground">{presence.displayName.length}/32</span>
            </div>
            <Input
              id="twin-avatar-display-name"
              value={presence.displayName}
              maxLength={32}
              onChange={(event) => presence.setDisplayName(event.target.value)}
              aria-invalid={!nameValidation.valid}
              aria-describedby={!nameValidation.valid ? 'twin-avatar-name-error' : undefined}
            />
            {!nameValidation.valid && (
              <p id="twin-avatar-name-error" className="flex items-start gap-1.5 text-xs text-destructive" role="alert">
                <CircleAlertIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                {nameValidation.error}
              </p>
            )}
          </section>

          <section className="space-y-2" aria-labelledby="avatar-colour-heading">
            <Label id="avatar-colour-heading">Avatar colour</Label>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="avatar-colour-heading">
              {AVATAR_COLORS.map((color) => {
                const selected = presence.avatarColor === color;
                return (
                  <button
                    key={color}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={`Select ${color} avatar colour`}
                    className={cn('flex size-10 items-center justify-center rounded-full border-2 transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', selected ? 'border-foreground' : 'border-transparent')}
                    style={{ backgroundColor: color }}
                    onClick={() => presence.setAvatarColor(color)}
                  >
                    {selected && <CheckCircle2Icon className="size-4 text-white drop-shadow" aria-hidden />}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="space-y-2" aria-labelledby="location-interval-heading">
            <div className="flex items-center justify-between gap-3">
              <Label id="location-interval-heading">Location update frequency</Label>
              <Badge variant="secondary">Every {presence.updateIntervalSeconds} seconds</Badge>
            </div>
            <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-labelledby="location-interval-heading">
              {UPDATE_INTERVALS.map((seconds) => (
                <Button key={seconds} type="button" size="sm" variant={presence.updateIntervalSeconds === seconds ? 'default' : 'outline'} role="radio" aria-checked={presence.updateIntervalSeconds === seconds} onClick={() => presence.setUpdateIntervalSeconds(seconds)}>
                  {seconds}s
                </Button>
              ))}
            </div>
          </section>

          <section className="rounded-lg border bg-muted/30 p-3" aria-live="polite">
            <div className="flex items-start gap-2">
              <MapPinIcon className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium">Location accuracy</p>
                <p className="text-xs text-muted-foreground">{accuracyLabel}</p>
                {coordinatesLabel && <p className="font-mono text-xs text-muted-foreground">Coordinates {coordinatesLabel}</p>}
                {locationTrack.location && <p className="text-xs text-muted-foreground">Latest fix {new Date(locationTrack.location.timestamp).toLocaleTimeString()}</p>}
                {(locationTrack.error ?? presence.error) && <p className="text-xs text-destructive">{locationTrack.error ?? presence.error}</p>}
              </div>
            </div>
          </section>

          <Button type="button" className="w-full" variant={presence.isSharing ? 'destructive' : 'default'} disabled={!presence.isSharing && !nameValidation.valid} onClick={presence.isSharing ? presence.leave : presence.join}>
            <RadioIcon className="size-4" aria-hidden />
            {presence.isSharing ? 'Stop Sharing Location' : 'Join Digital Twin'}
          </Button>
          <p className="text-center text-xs text-muted-foreground">{presence.isSharing ? 'Your live position is active for this scene.' : 'Your location stays private until you choose to join.'}</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
